import { Component, Inject } from "@angular/core";
import { alertSortOptionsByState, Domain, SECURITY_DOMAIN } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { initialStateParams } from "@app2/alert/alert-inbox/constants";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { InboxDropdownFilterType, ModeType, SortType, StateParams, StateType } from "@app2/fraud/type-defs";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { RouterService } from "@app2/shared/services/router.service";
import { Keys, StorageService } from "@app2/shared/services/storage.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { cloneDeep } from "lodash-es";

@UntilDestroy()
@Component({
    selector: "fraud-inbox-page",
    template: `
        <alert-inbox-header *ngIf="showInboxFilters()"
                            [selectedMode]="selectedMode"
                            [selectedDropdownFilter]="selectedDropdownFilter">
        </alert-inbox-header>
        <alert-state-filter *ngIf="selectedMode !== 'alert_story' && showInboxFilters()"
                            [selectedState]="selectedState">
        </alert-state-filter>

        <article class="inbox-main-sheet"
                 [ngClass]="{'story-mode': selectedMode === 'alert_story',
                             'fraud-alert-view': selectedMode === 'alert' && productDomain === 'FRAUD'}">
            <section class="inbox-main-container">
                <trigger-mode-viewer *ngIf="selectedMode === 'trigger'"
                                     [dropdownFilter]="selectedDropdownFilter"
                                     [state]="selectedState"
                                     [sort]="selectedSort"
                                     [selectedTriggerId]="selectedId"
                                     (onTriggerSelected)="onSelected($event)">
                </trigger-mode-viewer>
                <ng-container *ngIf="selectedMode === 'alert'">
                    <ng-container *ngIf="productDomain === 'FRAUD'; else notSingleAlertView">
                        <single-alert-view [alertId]="selectedId">
                        </single-alert-view>
                    </ng-container>
                    <ng-template #notSingleAlertView>
                        <alert-mode-viewer [state]="selectedState"
                                           [sort]="selectedSort"
                                           [selectedAlertId]="selectedId"
                                           (selectedAlertIdChange)="onSelected($event)">
                        </alert-mode-viewer>
                    </ng-template>
                </ng-container>
                <alert-story-mode-viewer *ngIf="selectedMode === 'alert_story'"
                                         [selectedAggregateValue]="selectedId">
                </alert-story-mode-viewer>
            </section>
        </article>
    `,
})
export class AlertInboxPageComponent {
    // These come from the Query Params
    selectedDropdownFilter: InboxDropdownFilterType;
    selectedMode: ModeType;
    selectedState: StateType;
    selectedSort: SortType;
    selectedId: uuid | string; // Alert or Trigger id or Aggregate value

    constructor(@Inject(PRODUCT_DOMAIN) public productDomain: Domain,
                private readonly alertClient: AlertClientService,
                private readonly notificationsService: NotificationsService,
                private readonly router: RouterService,
                private readonly storage: StorageService) {

        router.getQueryParams$()
            .pipe(untilDestroyed(this))
            .subscribe((queryParams: StateParams) => {
                // The first time we may not have the required params
                if (!queryParams.mode) {
                    let updateStateParams: StateParams = cloneDeep(initialStateParams);

                    let dropdownFilter = this.storage.get(Keys.alertInboxDropdownFilter);
                    if (dropdownFilter !== undefined) {
                        updateStateParams.dropdownFilter = dropdownFilter as InboxDropdownFilterType;
                        this.selectedDropdownFilter = updateStateParams.dropdownFilter;
                    }

                    let sortBy = this.storage.get(Keys.alertSortBy);
                    const validSorts = alertSortOptionsByState[updateStateParams.state];
                    if (sortBy !== undefined && validSorts.find(sortOption => sortBy === sortOption.value)) {
                        updateStateParams.sort = sortBy as SortType;
                        this.selectedSort = updateStateParams.sort;
                    }

                    router.replaceParams<StateParams>(updateStateParams, { replaceUrl: true });

                    return;
                }

                this.selectedMode = queryParams.mode;
                this.selectedDropdownFilter = queryParams.dropdownFilter;
                this.selectedSort = queryParams.sort;
                this.selectedState = queryParams.state;
                this.selectedId = queryParams.selected;
            });
    }

    onSelected(selected: uuid) {
        this.router.mergeParams<StateParams>({ selected });
    }

    showInboxFilters() {
        if (this.productDomain === SECURITY_DOMAIN) {
            return true;
        } else {
            return this.selectedMode !== "alert";
        }
    }
}
